import type { Ssm5ConnectErrorCode } from '@packages/domain/ssm5';
import type { TextInputErrorPresentation } from '@packages/ui/shared';

import { colors } from '@packages/core/styles';
import { isValidCloudConnectorId, normalizeCloudConnectorId } from '@packages/domain/ssm5';
import { Button, TextInputHelper, Typography } from '@packages/ui/shared';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import type { Ssm5ConnectFailure } from '../context/ssm5-connection.context';

import { ssm5ControlStyles, ssm5TextStyles } from '../ssm5.styles';
import { PasteFromClipboardButton } from './paste-from-clipboard-button';

const CLOUD_CONNECTOR_ID_CONTROL_ID = 'ssm5-cloud-connector-id';
const CARD_BORDER_WIDTH = 1;
const CARD_INSET = 24;

/** The message under the field for each way a connect attempt can fail. */
const CONNECT_ERROR_KEYS: Record<Ssm5ConnectErrorCode, string> = {
    invalidId: 'ssm5:connection.errors.invalidId',
    network: 'ssm5:connection.errors.network',
    notPaired: 'ssm5:connection.errors.notPaired',
    unauthorized: 'ssm5:connection.errors.unauthorized',
    unknown: 'ssm5:connection.errors.unknown',
    vehicleNotFound: 'ssm5:connection.errors.vehicleNotFound',
    vinReadFailed: 'ssm5:connection.errors.vinReadFailed',
};

/** The error look of the Cloud Connector ID field: a thin red outline and the message under it. */
const FIELD_ERROR_PRESENTATION: TextInputErrorPresentation = {
    borderWidth: 1,
    messageStyle: ssm5TextStyles.fieldError,
    showIcon: false,
};

const useStyles = createUseStyles({
    card: {
        backgroundColor: colors.white,
        border: `${CARD_BORDER_WIDTH}px solid ${colors.grayThree}`,
        borderRadius: 8,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        padding: CARD_INSET - CARD_BORDER_WIDTH,
    },
});

/** Props for {@link ConnectToSsm5Card}. */
type ConnectToSsm5CardProps = {
    /** The failed attempt to explain; its ID stays in the field, locked until it is changed. */
    failure?: Ssm5ConnectFailure;
    /** Locks the Connect action regardless of input validity. */
    isConnectDisabled?: boolean;
    /** Receives the normalized Cloud Connector ID. */
    onConnect: (cloudConnectorId: string) => void;
};

/**
 * Card with the Cloud Connector ID field and the Connect action.
 *
 * @param props - {@link ConnectToSsm5CardProps}
 * @returns the connect card
 */
export function ConnectToSsm5Card({ failure, isConnectDisabled = false, onConnect }: ConnectToSsm5CardProps) {
    const classes = useStyles();
    const { t } = useTranslation();
    const [cloudConnectorId, setCloudConnectorId] = useState(failure?.cloudConnectorId ?? '');
    const [isTouched, setIsTouched] = useState(false);

    const isValid = isValidCloudConnectorId(cloudConnectorId);
    const isFailedId =
        failure !== undefined && normalizeCloudConnectorId(cloudConnectorId) === failure.cloudConnectorId;
    const isConnectLocked = isConnectDisabled || !isValid || isFailedId;
    const errorKey = fieldErrorKey(cloudConnectorId, isTouched, isFailedId ? failure : undefined);

    const markTouched = useCallback(() => setIsTouched(true), []);
    const pasteCloudConnectorId = useCallback((text: string) => {
        setCloudConnectorId(text.trim());
        setIsTouched(true);
    }, []);
    const connect = useCallback(
        () => onConnect(normalizeCloudConnectorId(cloudConnectorId)),
        [cloudConnectorId, onConnect]
    );

    return (
        <section className={classes.card}>
            <Typography color="textDarkSecondary" style={ssm5TextStyles.body} variant="default">
                {t('ssm5:connect.description')}
            </Typography>
            <TextInputHelper
                controlId={CLOUD_CONNECTOR_ID_CONTROL_ID}
                errorMessage={errorKey && t(errorKey)}
                errorPresentation={FIELD_ERROR_PRESENTATION}
                inputContainerStyle={ssm5ControlStyles.field}
                label={t('ssm5:connect.idLabel')}
                labelStyle={ssm5TextStyles.fieldLabel}
                onBlur={markTouched}
                onChangeText={setCloudConnectorId}
                placeholder={t('ssm5:connect.idPlaceholder')}
                rightIcon={<PasteFromClipboardButton onPaste={pasteCloudConnectorId} title={t('ssm5:connect.paste')} />}
                textInputStyle={ssm5TextStyles.fieldText}
                value={cloudConnectorId}
            />
            <Button
                block
                disabled={isConnectLocked}
                onPress={connect}
                style={[ssm5ControlStyles.primaryButton, isConnectLocked && ssm5ControlStyles.primaryButtonDisabled]}
                title={t('ssm5:connect.button')}
                titleStyle={ssm5TextStyles.primaryButtonTitle}
            />
        </section>
    );
}

/**
 * Pick the message under the field: the shape error once the field was left with an invalid value,
 * else the failed attempt's error while its ID is still the one in the field.
 *
 * @param cloudConnectorId - the raw field value
 * @param isTouched - whether the field was left or pasted into
 * @param failure - the failed attempt whose ID is in the field, if any
 * @returns the translation key of the message, or undefined when there is nothing to show
 */
function fieldErrorKey(cloudConnectorId: string, isTouched: boolean, failure?: Ssm5ConnectFailure): string | undefined {
    if (isTouched && cloudConnectorId.trim() !== '' && !isValidCloudConnectorId(cloudConnectorId)) {
        return 'ssm5:connect.invalidIdError';
    }

    return failure && CONNECT_ERROR_KEYS[failure.errorCode];
}
